import {
  AUTHORIZATION_STATUSES,
  AUTHORIZATION_STATUSES_TO_TEXT,
} from 'src/common/Status/authorizationConstants';

/**
 * Determines the augmented authorization status text based on lineage.
 *
 * @param {Object} authorization - The current authorization object
 * @param {Array} lineageAuthorizations - Array of all authorizations in the lineage
 * @returns {string} Human-readable status text
 */
const getAugmentedAuthorizationStatus = (authorization, lineageAuthorizations = []) => {
  const state = authorization.state;

  const statusTexts = {
    ...AUTHORIZATION_STATUSES_TO_TEXT,
    [AUTHORIZATION_STATUSES.open]: 'Open',
  };

  // Only accepted authorizations get augmented. Every other state already
  // represents the latest request in the chain, so we preserve the base status text.
  if (state !== AUTHORIZATION_STATUSES.accepted || lineageAuthorizations.length === 1) {
    return statusTexts[state];
  }

  const currentAuthIndex = lineageAuthorizations.findIndex(
    (auth) => auth.id === authorization.id,
  );
  // If lineage is incomplete, avoid inferring an augmented label from partial context.
  if (currentAuthIndex < 0) {
    return statusTexts[state];
  }

  const olderAuthorizations = lineageAuthorizations.slice(0, currentAuthIndex);

  const newerAuthorizations = lineageAuthorizations.slice(currentAuthIndex + 1);

  const acceptedText = statusTexts[AUTHORIZATION_STATUSES.accepted];

  // Check for newer requested or in_review authorizations
  const hasNewerRequestedOrInReview = newerAuthorizations.some(
    (auth) => [AUTHORIZATION_STATUSES.open, AUTHORIZATION_STATUSES.inReview].includes(auth.state),
  );

  if (hasNewerRequestedOrInReview) {
    return `${acceptedText} (Changes Requested)`;
  }

  // Check if only denied authorizations are newer
  const onlyDenied = newerAuthorizations.length > 0 && newerAuthorizations.every(
    (auth) => auth.state === AUTHORIZATION_STATUSES.rejected,
  );

  if (onlyDenied) {
    return `${acceptedText} (Changes Rejected)`;
  }

  // Accepted (Updated) applies when any older authorization in the lineage was voided.
  const hasOlderVoidAuthorization = olderAuthorizations.some(
    (auth) => auth.state === AUTHORIZATION_STATUSES.void,
  );

  if (hasOlderVoidAuthorization) {
    return `${acceptedText} (Updated)`;
  }

  if (newerAuthorizations.length === 0) {
    return acceptedText;
  }

  return statusTexts[state];
};

export default getAugmentedAuthorizationStatus;
